import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Lock } from "lucide-react";

import type { EventRow } from "@/lib/api/events";
import type { ParticipantRow } from "@/lib/api/participants";
import {
  computeEventTiming,
  formatDurationLong,
  formatHms,
  type EventTiming,
} from "@/lib/event-state";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { InlineAlert } from "@/components/ui/States";

function useTiming(event: EventRow): EventTiming {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return useMemo(() => computeEventTiming(event, now), [event, now]);
}

/* ── State A · Pre-reveal countdown ──────────────────────────────────── */

export function PreRevealState({
  event,
  participants,
}: {
  event: EventRow;
  participants: ParticipantRow[];
}) {
  const timing = useTiming(event);
  const { h, m, s } = formatHms(timing.msToReveal);
  const isLastHalfHour = timing.msToReveal <= 30 * 60_000 && timing.msToReveal > 0;

  const total = participants.length;
  const submitted = participants.filter((p) => p.pitch_status !== "pending").length;
  const approved = participants.filter((p) => p.pitch_status === "approved").length;
  const flaggedNeedingReview = participants.filter(
    (p) => p.pitch_status === "flagged" || p.pitch_status === "submitted",
  ).length;
  const conversionPct = total > 0 ? Math.round((submitted / total) * 100) : 0;

  return (
    <div className="flex flex-col gap-5">
      {flaggedNeedingReview > 0 ? (
        <InlineAlert tone="danger">
          <strong>{flaggedNeedingReview} pitch{flaggedNeedingReview === 1 ? "" : "es"} still need review.</strong>{" "}
          Open the Pitches tab — these won&apos;t auto-publish.
        </InlineAlert>
      ) : null}
      {conversionPct < 60 && total > 0 ? (
        <InlineAlert tone="warning">
          <strong>Last chance to send reminders.</strong> Pitch conversion is {conversionPct}% —
          a final nudge usually lifts it before reveal.
        </InlineAlert>
      ) : null}

      <Card padding="lg" className="text-center">
        <p className="text-[12px] font-bold uppercase tracking-[1.2px] text-[#2563eb]">
          Matches reveal in
        </p>
        <div
          className={
            "mt-4 font-bold tabular-nums leading-none tracking-tighter text-[#1a1a1a] " +
            (isLastHalfHour
              ? "text-[64px] sm:text-[80px] animate-pulse"
              : "text-[56px] sm:text-[72px]")
          }
        >
          {h}:{m}:{s}
        </div>
        <div className="mt-3 flex justify-center gap-12 text-[11px] uppercase tracking-[1px] text-[#94a3b8]">
          <span>Hours</span>
          <span>Minutes</span>
          <span>Seconds</span>
        </div>
        <p className="mt-5 text-[14px] text-[#475569]">
          <strong className="text-[#1a1a1a]">{total} participant{total === 1 ? "" : "s"}</strong> are about to see their matches.
          <br />
          No more changes can be made before reveal.
        </p>
      </Card>

      <div className="flex items-start gap-3 rounded-[10px] border border-[#e2e8f0] bg-[#f8fafc] p-4 text-[13px] text-[#475569]">
        <Lock className="h-4 w-4 shrink-0 text-[#6c757d]" />
        <span>
          <strong className="text-[#1a1a1a]">Settings are locked.</strong> Event details,
          participant list, and pitches cannot be modified during the final 6 hours before reveal.
        </span>
      </div>

      <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-[#6c757d]">
        Final readiness
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ReadinessTile label="Registered" value={String(total)} sub="of invited" />
        <ReadinessTile
          label="Pitches"
          value={String(submitted)}
          sub={`${conversionPct}% conversion`}
        />
        <ReadinessTile
          label="Approved"
          value={String(approved)}
          sub={approved === submitted ? "all clear" : `${submitted - approved} pending`}
        />
      </div>
    </div>
  );
}

function ReadinessTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] p-4">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#6c757d]">{label}</div>
      <div className="mt-1 text-[22px] font-bold tabular-nums leading-none text-[#1a1a1a]">
        {value}
      </div>
      {sub ? <div className="mt-1 text-[11px] text-[#94a3b8]">{sub}</div> : null}
    </div>
  );
}

/* ── State B · Live event ────────────────────────────────────────────── */

export function LiveState({
  event,
  participants,
  matchesRevealed,
  matchesOpened,
  markedAsMet,
  recentActivity,
  onReportIssue,
}: {
  event: EventRow;
  participants: ParticipantRow[];
  matchesRevealed: number;
  matchesOpened: number;
  markedAsMet: number;
  recentActivity: { time: string; icon: string; text: string }[];
  onReportIssue: () => void;
}) {
  const timing = useTiming(event);
  const elapsed = Math.max(0, Date.now() - timing.eventStartMs);
  const remaining = Math.max(0, timing.eventEndMs - Date.now());
  const total = participants.length;
  const avgConnections =
    total > 0 ? (markedAsMet * 2 / total).toFixed(1) : "0.0";

  return (
    <div className="flex flex-col gap-5">
      <Card padding="lg" className="border-[#fde68a] bg-[#fffbeb] text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fef3c7] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.6px] text-[#92400e]">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#f59e0b]" />
          Live now
        </span>
        <p className="mt-3 text-[28px] font-bold tracking-tight text-[#1a1a1a]">Event is live</p>
        <p className="mt-1 text-[14px] text-[#475569]">
          {total} participants · {matchesRevealed} mutual matches revealed
        </p>
        <p className="mt-1 text-[13px] font-semibold text-[#92400e]">
          Started {formatDurationLong(elapsed)} ago · ends in {formatDurationLong(remaining)}
        </p>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <LiveMetric label="Matches opened" value={String(matchesOpened)} accent />
        <LiveMetric label="Marked as met" value={String(markedAsMet)} accent />
        <LiveMetric label="Active participants" value={String(total)} />
        <LiveMetric label="Avg connections" value={avgConnections} />
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-[#6c757d]">
          Recent activity
        </p>
        <div className="mt-2 rounded-[12px] border border-[#e2e8f0] bg-white">
          {recentActivity.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-[#94a3b8]">
              No activity yet — events will show here as people open matches and mark connections.
            </p>
          ) : (
            recentActivity.map((row, i) => (
              <div
                key={`${row.time}-${i}`}
                className="flex items-center gap-3 border-b border-[#e2e8f0] px-4 py-2.5 text-[13px] text-[#475569] last:border-b-0"
              >
                <span className="w-12 text-[11px] tabular-nums text-[#94a3b8]">{row.time}</span>
                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#f8fafc] text-[12px]">
                  {row.icon}
                </span>
                <span className="flex-1">{row.text}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <Button
        onClick={onReportIssue}
        variant="secondary"
        size="md"
        leadingIcon={<AlertTriangle className="h-4 w-4 text-[#dc2626]" />}
        className="border-[#fecaca] text-[#dc2626] hover:bg-[#fef2f2]"
      >
        Report an issue
      </Button>
    </div>
  );
}

function LiveMetric({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        "rounded-[12px] border p-4 " +
        (accent
          ? "border-[#fde68a] bg-[#fffbeb]"
          : "border-[#e2e8f0] bg-[#f8fafc]")
      }
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#6c757d]">{label}</div>
      <div className="mt-1 text-[28px] font-bold tabular-nums leading-none text-[#1a1a1a]">
        {value}
      </div>
    </div>
  );
}

/* ── State C · Wrapping up ───────────────────────────────────────────── */

export function WrappingUpState({
  event,
  surveyCounts,
  preliminaryMetrics,
  reminderSentAt,
  onSendReminder,
  onViewEarlyReport,
}: {
  event: EventRow;
  surveyCounts: { responded: number; total: number };
  preliminaryMetrics: {
    totalMatches: number;
    markedAsMet: number;
    nps: number | null;
    fiveStarPct: number | null;
  };
  reminderSentAt: number | null;
  onSendReminder: () => void;
  onViewEarlyReport: () => void;
}) {
  const timing = useTiming(event);
  const responsePct =
    surveyCounts.total > 0
      ? Math.round((surveyCounts.responded / surveyCounts.total) * 100)
      : 0;
  const finalReportEtaMs = timing.wrappingUpEndMs - Date.now();
  const reminderRecentlySent =
    reminderSentAt !== null && Date.now() - reminderSentAt < 2 * 3_600_000;

  const matchConversion =
    preliminaryMetrics.totalMatches > 0
      ? Math.round(
          (preliminaryMetrics.markedAsMet / preliminaryMetrics.totalMatches) * 100,
        )
      : 0;

  return (
    <div className="flex flex-col gap-5">
      <Card padding="lg" className="border-[#ddd6fe] bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe]">
        <p className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">Event wrapped up</p>
        <p className="mt-1 text-[14px] text-[#475569]">
          Collecting feedback from participants. Final report will be ready once 70% respond or in
          24 hours.
        </p>

        <div className="mt-5 rounded-[12px] bg-white/60 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] font-semibold text-[#1a1a1a]">Survey responses</span>
            <span className="text-[14px] font-bold tabular-nums text-[#6d28d9]">
              {surveyCounts.responded} of {surveyCounts.total} · {responsePct}%
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#7c3aed]/15">
            <div
              className="h-full rounded-full bg-[#7c3aed] transition-[width] duration-500"
              style={{ width: `${Math.min(100, responsePct)}%` }}
            />
          </div>
          <p className="mt-2 text-[12px] text-[#94a3b8]">
            Final report ready in ~{formatDurationLong(Math.max(0, finalReportEtaMs))}
          </p>
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        <Button
          onClick={onSendReminder}
          disabled={reminderRecentlySent}
          size="md"
          fullWidth
          className="bg-[#7c3aed] hover:bg-[#6d28d9]"
        >
          {reminderRecentlySent
            ? `Reminder sent ${formatDurationLong(Date.now() - (reminderSentAt ?? 0))} ago`
            : "Send survey reminder"}
        </Button>
        <Button onClick={onViewEarlyReport} variant="secondary" size="md" fullWidth>
          View early report
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-[#6c757d]">
          Preliminary metrics
        </p>
        <span className="rounded-full bg-[#fef3c7] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.4px] text-[#92400e]">
          ⏳ Updating
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <PrelimMetric
          label="Total matches"
          value={String(preliminaryMetrics.totalMatches)}
          sub={`${(preliminaryMetrics.totalMatches / Math.max(1, surveyCounts.total)).toFixed(1)} per person avg`}
        />
        <PrelimMetric
          label="Marked as met"
          value={String(preliminaryMetrics.markedAsMet)}
          sub={`${matchConversion}% match conversion`}
        />
        <PrelimMetric
          label="NPS (so far)"
          value={preliminaryMetrics.nps !== null ? `+${preliminaryMetrics.nps}` : "—"}
          sub={`based on ${surveyCounts.responded} responses`}
        />
        <PrelimMetric
          label="5-star rating"
          value={
            preliminaryMetrics.fiveStarPct !== null
              ? `${preliminaryMetrics.fiveStarPct}%`
              : "—"
          }
          sub={`based on ${surveyCounts.responded} responses`}
        />
      </div>

      <div className="flex items-start gap-3 rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] p-4 text-[13px] text-[#475569]">
        <span className="text-[16px]">ℹ</span>
        <span>
          <strong className="text-[#1a1a1a]">Why preliminary?</strong> Some metrics depend on
          participant survey responses. We wait until 70% respond — usually within a day — to
          produce the final report. You can view an early version anytime.
        </span>
      </div>
    </div>
  );
}

function PrelimMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] p-4">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#6c757d]">{label}</div>
      <div className="mt-1 text-[24px] font-bold tabular-nums leading-none text-[#1a1a1a]">
        {value}
      </div>
      {sub ? <div className="mt-1 text-[12px] text-[#94a3b8]">{sub}</div> : null}
    </div>
  );
}
